import { h, Fragment } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { trapFocus, removeTrapFocus, getVisibleElements } from './../utils/focus';

const Panel = ({
  children,
  alignment = 'right',
  animation = 'slide',
  context = null,
  triggers,
  defaultOpen = false,
  additionalClasses = '',
  showBackdrop = false,
  offsetTop,
  ID = null,
  focusOutElement = null,
  keyClose = false,
  overscrollNone = false,
}) => {
  const { openTriggerEl, closeTriggerEl, toggleEl } = triggers;
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const handleOpenEvent = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(true);
    document.body.style.overflow = overscrollNone ? 'hidden' : '';
    if (ID) {
      setTimeout(() => {        
        const container = document.querySelector(`#${ID}`);
        const focusableElements = getVisibleElements(container.querySelectorAll('button, input, select, textarea, [tabindex]:not([tabindex="-1"])'))
        trapFocus(container, focusableElements[0]);
      }, 400);
    }
  };
  const handleCloseEvent = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(false);
    if (document.hasFocus() && focusOutElement) {
      removeTrapFocus(document.querySelector(`${focusOutElement}`));
    }
  };
  const handleToggleEvent = () => setIsOpen(!isOpen);
  const handleCloseAllPanels = () => {
    setIsOpen(false)
  }
  const handleKeyPressEvent = (e) => {
    if (keyClose) {
      const { key } = e;
      const escKeyPressed = key && (key === 'Escape' || key === 'Esc');
      
      if (escKeyPressed && isOpen) {
        setIsOpen(false);
        if (document.hasFocus() && focusOutElement) {
          removeTrapFocus(document.querySelector(`${focusOutElement}`));
        }
      }
    }
  };

  useEffect(() => {
    if (openTriggerEl) openTriggerEl.addEventListener('click', handleOpenEvent);
    if (closeTriggerEl)
      closeTriggerEl.addEventListener('click', handleCloseEvent);
    if (toggleEl) toggleEl.addEventListener('click', handleToggleEvent);
    if (context) {
      document.addEventListener(`${context}:open`, handleOpenEvent);
      document.addEventListener(`${context}:close`, handleCloseEvent);
    }
    if (keyClose) document.addEventListener('keydown', handleKeyPressEvent);
    document.addEventListener('close-all-panels', handleCloseAllPanels);

    return () => {
      if (openTriggerEl)
        openTriggerEl.removeEventListener('click', handleOpenEvent);
      if (closeTriggerEl)
        closeTriggerEl.removeEventListener('click', handleCloseEvent);
      if (toggleEl) toggleEl.removeEventListener('click', handleToggleEvent);
      if (context) {
        document.removeEventListener(`${context}:open`, handleOpenEvent);
        document.removeEventListener(`${context}:close`, handleCloseEvent);
      }
      if (keyClose) document.removeEventListener('keydown', handleKeyPressEvent);
      document.removeEventListener('close-all-panels', handleCloseAllPanels);
    };
  }, [openTriggerEl, closeTriggerEl, toggleEl, isOpen]);

  useEffect(() => {
    document.dispatchEvent(
      new CustomEvent(`${context}:toggle`, {
        detail: {
          open: isOpen,
        },
      }),
    );
  }, [isOpen]);

  const isFadeAnimation = animation === 'fade';
  const closedClass =
    alignment === 'right'
      ? 'translate-x-full'
      : isFadeAnimation
      ? 'opacity-0 top-[50px]'
      : '-translate-x-full';

  return (
    <>
      {showBackdrop && (
        <div
          className={`${isFadeAnimation ? 'duration-[800ms]' : ''} ${
            isOpen
              ? 'w-full h-full fixed left-0 top-0 opacity-30 z-50 bg-black/60'
              : 'opacity-0 -z-1'
          }`}
          style={{ top: `${offsetTop}px` }}
          onClick={() => {
            document.body.style.overflow = '';
            setIsOpen(false);
          }}
        />
      )}
      <div
        id={ID ? ID : ''}
        className={`
        ${isOpen ? 'pointer-events-all' : 'pointer-events-none'}
        block
        max-w-panel
        w-full h-full
        fixed
        top-0
        bottom-0
        ${
          isFadeAnimation
            ? 'transition-opacity duration-[400ms]'
            : 'transition-transform'
        }
        z-50
        bg-white
        ${alignment === 'right' ? 'right-0' : 'left-0'}
        ${isOpen ? 'translate-x-0 opacity-100' : closedClass} 
        ${additionalClasses}
        `}
        style={{ top: `${offsetTop}px` }}
      >
        {children}
      </div>
    </>
  );
};

export default Panel;
